import React from 'react';
import { css } from '@emotion/react';
import { useCtaProvider } from '../../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import RatingSummary from '../../franchise/nearest/rating-summary';
import Color from '../../primitives/colors';
import Skeleton from '../../primitives/skeleton';
import RowSet from '../../primitives/grid/row-set';
import Typography from '../../primitives/typography';
import StyledPillButton, { StyledPillButtonAsLink } from '../../primitives/styled-pill-button';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import triggerInvoca from '../../utils/trigger-invoca';
import ChangeLocationDrawer from '../../franchise/nearest/change-location-drawer';
import PhoneNumber from '../../primitives/phone-number';
import { useLocator } from '../../../contexts/franchise/locator-provider';
import {
    AVERAGE_RATE_THRESHOLD,
    CHANGE_LOCATION,
    LOCAL_CITY_LOCATION,
    NATIONAL_CALL_CENTER_LOCATION, PHONE_NUMBER, PHONE_NUMBER_DISPLAY,
    YOUR_DETECTED_LOCATION,
    YOUR_SELECTED_LOCATION,
    NATIONAL_CALL_CENTER_DISPLAY,
} from '../../../constants';
import formatPhoneLink from '../../utils/format-phone-link';

const styles = {
    headerDropdownContainer: css({
        padding: '16px',
        width: 'min-content',
        maxWidth: '700px',
    }),
    headerDropdownInnerContainer: css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    }),
    locationTextRowSet: css({
        paddingRight: '16px',
    }),
    textSkeleton: css({ height: '32px' }),
    phoneSkeleton: css({
        width: '256px',
        height: '48px',
    }),
    actionButtonsRowSet: css({
        textAlign: 'center',
        width: 'unset',
    }),
};

const HeaderDropdown = (): JSX.Element => {
    const {
        loading: geoIsLoading,
        geo: locatorGeo,
        franchise: locatorFranchise,
        locationChangedByUser,
        isNationalCallCenter,
    } = useLocator();

    React.useEffect(() => {
        triggerInvoca();
    }, []);

    const [locationChangeVisible, setLocationChangeVisible] = React.useState<boolean>(false);

    const onChangeLocation = (): void => {
        setLocationChangeVisible(true);
    };

    const { hideFranchiseLink } = useCtaProvider();
    const { isLocalCityPage, franchise: micrositeFranchise } = useMicrosite();

    const geo = locatorGeo;
    const franchise = micrositeFranchise || locatorFranchise?.data;
    const disableChangeLocation = !!micrositeFranchise;

    const frId = franchise?.franchiseNumber;
    const frLink = franchise?.permalink;
    const reviewCount = franchise?.reviewCount;
    const averageRating = franchise?.averageRating;
    const frName = franchise?.name;
    const frPhone = franchise?.mainPhone;

    return (
        <div css={styles.headerDropdownContainer}>
            <ChangeLocationDrawer
                visible={locationChangeVisible}
                onClose={() => setLocationChangeVisible(false)}
            />
            <div>
                <div css={styles.headerDropdownInnerContainer}>
                    <RowSet breakpoints={{ dt: { between: 20 } }}>
                        <RowSet
                            breakpoints={{ dt: { between: 4 } }}
                            css={styles.locationTextRowSet}
                        >
                            <Color color="primaryOrange">
                                {(isLocalCityPage && !hideFranchiseLink) && LOCAL_CITY_LOCATION}
                                {(locationChangedByUser && !isLocalCityPage
                                        && !hideFranchiseLink && !isNationalCallCenter)
                                    && YOUR_SELECTED_LOCATION}
                                {(!locationChangedByUser && !isLocalCityPage
                                        && !hideFranchiseLink && !isNationalCallCenter)
                                    && YOUR_DETECTED_LOCATION}
                                {((hideFranchiseLink || isNationalCallCenter)
                                    && !isLocalCityPage) && NATIONAL_CALL_CENTER_LOCATION}
                            </Color>
                            {(!isNationalCallCenter && !isLocalCityPage && !locationChangedByUser) && (
                                <Skeleton css={styles.textSkeleton}>
                                    {(!geoIsLoading
                                        && geo?.city
                                        && !hideFranchiseLink
                                        && frName) && (
                                        <Typography fontSize="3xl" fontWeight="medium">
                                            {geo.city}
                                            {geo?.stateShort ? ', ' : ' '}
                                            {geo?.stateShort || ''}
                                        </Typography>
                                    )}
                                </Skeleton>
                            )}
                            {(!isNationalCallCenter && !isLocalCityPage && locationChangedByUser) && (
                                <Skeleton css={styles.textSkeleton}>
                                    {(!geoIsLoading
                                        && geo?.city
                                        && !hideFranchiseLink
                                        && frName) && (
                                        <Typography fontSize="3xl" fontWeight="medium">
                                            {frName}
                                        </Typography>
                                    )}
                                </Skeleton>
                            )}
                            {isLocalCityPage && (
                                <Skeleton css={styles.textSkeleton}>
                                    <Typography fontSize="3xl" fontWeight="medium">
                                        {franchise?.address?.city}
                                        {franchise?.address?.state ? ', ' : ' '}
                                        {franchise?.address?.state || ''}
                                    </Typography>
                                </Skeleton>
                            )}
                            {((hideFranchiseLink || isNationalCallCenter) && !isLocalCityPage) && (
                                <Typography fontSize="3xl" fontWeight="medium">
                                    {NATIONAL_CALL_CENTER_DISPLAY}
                                </Typography>
                            )}
                            {((!isNationalCallCenter && !hideFranchiseLink && !locationChangedByUser) || isLocalCityPage) && (
                                <Skeleton>
                                    {frName && <Typography fontSize="sm">{frName}</Typography>}
                                </Skeleton>
                            )}
                            {(reviewCount !== 0 && !hideFranchiseLink && !isNationalCallCenter && !!averageRating && averageRating >= AVERAGE_RATE_THRESHOLD && typeof reviewCount === 'number' && typeof averageRating === 'number') && (
                                <RatingSummary count={reviewCount} rating={averageRating} />
                            )}
                        </RowSet>
                        <Skeleton
                            css={styles.phoneSkeleton}
                            isButton
                            backgroundColor="primaryOrange"
                        >
                            {(frPhone && !hideFranchiseLink) && (
                                <PhoneNumber
                                    as={StyledButtonAsLink}
                                    to={`tel:${formatPhoneLink(frPhone) as string}`}
                                >
                                    {formatPhoneLink(frPhone)}
                                </PhoneNumber>
                            )}
                            {hideFranchiseLink && (
                                <PhoneNumber
                                    as={StyledButtonAsLink}
                                    to={`tel:${PHONE_NUMBER as string}`}
                                >
                                    {PHONE_NUMBER_DISPLAY}
                                </PhoneNumber>
                            )}
                        </Skeleton>
                    </RowSet>
                    <RowSet
                        breakpoints={{ dt: { between: 18 } }}
                        css={styles.actionButtonsRowSet}
                    >
                        {(!disableChangeLocation && !hideFranchiseLink && !isLocalCityPage) && (
                            <div>
                                Need help elsewhere?
                                {' '}
                                <StyledPillButton
                                    className="change-location-open-cta"
                                    onClick={onChangeLocation}
                                >
                                    {CHANGE_LOCATION}
                                    {' '}
                                    ›
                                </StyledPillButton>
                            </div>
                        )}
                        {isLocalCityPage && (
                            <div>
                                Need help elsewhere?
                                {' '}
                                <StyledPillButtonAsLink
                                    to="/locations"
                                >
                                    {CHANGE_LOCATION}
                                    {' '}
                                    ›
                                </StyledPillButtonAsLink>
                            </div>
                        )}
                        <Typography fontSize="xl" fontWeight="medium" color="primaryOrange">
                            24/7 Emergency Service
                        </Typography>
                        {!!frId && !hideFranchiseLink && frLink && (
                            <StyledPillButtonAsLink
                                to={frLink}
                            >
                                Find Out More ›
                            </StyledPillButtonAsLink>
                        )}
                    </RowSet>
                </div>
            </div>
        </div>
    );
};
export default HeaderDropdown;
